import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Trash2, Loader2, AlertTriangle,
  Download, RefreshCw, X, ChevronDown, ChevronUp,
  Check, Pencil
} from 'lucide-react';
import { contentService } from '../../services/ContentService';
import { api } from '../../lib/api';
import ContentCard from '../../components/contents/batches/ContentCard';
import CarouselViewer from '../../components/contents/carousel/CarouselViewer';
import PublishToSocialModal from '../../components/social/PublishToSocialModal';
import { getGridSpan, isBatchActive } from '../../lib/contentHelpers';
import { Sparkles } from 'lucide-react';

const ContentBatchViewer = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  
  const [batch, setBatch] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Estado para edição do título
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  
  // Estado para mostrar/ocultar prompt
  const [showPrompt, setShowPrompt] = useState(false);
  
  // Estado do modal de publicação
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [contentToPublish, setContentToPublish] = useState(null);

  // Buscar batch
  const fetchBatch = useCallback(async () => {
    try {
      const response = await contentService.getBatch(batchId);
      setBatch(response.data);
      setEditedTitle(response.data.title || '');
      setError(null);
    } catch (err) {
      console.error('Erro ao buscar projeto:', err);
      setError(err.response?.data?.detail || err.message || 'Não foi possível carregar o projeto.');
    } finally {
      setIsLoading(false);
    }
  }, [batchId]);

  // Carregar inicialmente
  useEffect(() => {
    fetchBatch();
  }, [fetchBatch]);

  // Polling se batch estiver ativo OU se algum conteúdo está sendo editado (Fase 4)
  useEffect(() => {
    if (!batch) return;

    const hasEditingContent = (batch.contents || []).some(
      c => (c.metadata?.edit_job?.status) === 'processing'
    );
    const isActive = isBatchActive(batch) || hasEditingContent;

    if (isActive) {
      const interval = setInterval(() => {
        fetchBatch();
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [batch, fetchBatch]);

  // Salvar título editado
  const handleSaveTitle = async () => {
    if (!editedTitle.trim() || editedTitle === batch.title) {
      setIsEditingTitle(false);
      setEditedTitle(batch.title);
      return;
    }

    setIsSavingTitle(true);
    try {
      // Chamada direta à API para atualizar o título
      await api.patch(`/api/contents/batches/${batchId}`, { title: editedTitle.trim() });
      setBatch(prev => ({ ...prev, title: editedTitle.trim() }));
      setIsEditingTitle(false);
    } catch (err) {
      console.error('Erro ao salvar título:', err);
      alert('Erro ao salvar título. Tente novamente.');
      setEditedTitle(batch.title);
    } finally {
      setIsSavingTitle(false);
    }
  };

  // Cancelar edição do título
  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditedTitle(batch.title);
  };

  // Deletar batch
  const handleDeleteBatch = async () => {
    const confirmDelete = window.confirm(
      `Tem certeza que deseja excluir "${batch.title}"? Esta ação não pode ser desfeita.`
    );

    if (!confirmDelete) return;

    setIsDeleting(true);

    try {
      await contentService.deleteBatch(batchId);
      navigate('/contents');
    } catch (err) {
      console.error('Erro ao excluir projeto:', err);
      alert(`Erro ao excluir: ${err.response?.data?.detail || err.message}`);
      setIsDeleting(false);
    }
  };

  // Download de todos os conteúdos (via fetch+blob para forçar download cross-origin)
  const handleDownloadAll = () => {
    if (!batch || !batch.contents) return;

    const completedContents = batch.contents.filter(c => c.status === 'completed' && c.result_url);

    if (completedContents.length === 0) {
      alert('Nenhum conteúdo disponível para download ainda.');
      return;
    }

    completedContents.forEach((content, index) => {
      setTimeout(async () => {
        try {
          const response = await fetch(content.result_url);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = content.file_name || `content_${index + 1}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (err) {
          window.open(content.result_url, '_blank');
        }
      }, index * 800);
    });
  };

  // Preview modal
  const handlePreview = (content) => {
    setPreviewContent(content);
  };

  const closePreview = () => {
    setPreviewContent(null);
  };

  // Abrir modal de publicação
  const handleOpenPublishModal = (content) => {
    setContentToPublish(content);
    setPublishModalOpen(true);
  };

  // Fechar modal de publicação
  const handleClosePublishModal = () => {
    setPublishModalOpen(false);
    setContentToPublish(null);
  };

  // Callback quando publicação for bem sucedida
  const handlePublishSuccess = (platform, contentId, result) => {
    console.log('Publicação bem sucedida:', { platform, contentId, result });
    
    setBatch(prevBatch => {
      if (!prevBatch || !prevBatch.contents) return prevBatch;
      
      return {
        ...prevBatch,
        contents: prevBatch.contents.map(content => {
          if (content.id === contentId) {
            const currentPublished = content.published_to || [];
            if (!currentPublished.includes(platform)) {
              return {
                ...content,
                published_to: [...currentPublished, platform]
              };
            }
          }
          return content;
        })
      };
    });

    setTimeout(() => {
      handleClosePublishModal();
      fetchBatch();
    }, 500);
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Carregando projeto...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Erro ao carregar</h2>
          <p className="text-sm text-gray-500 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={fetchBatch}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
            >
              Tentar novamente
            </button>
            <Link
              to="/contents"
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              Voltar
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!batch) return null;

  const completedCount = batch.contents?.filter(c => c.status === 'completed').length || 0;
  const totalCount = batch.contents?.length || 0;
  const processingCount = batch.contents?.filter(c => c.status === 'processing').length || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-6">
        
        {/* Project Info Card */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
          <div className="flex items-start justify-between gap-4">

            {/* Título editável */}
          <div className="mb-4">
            {isEditingTitle ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="flex-1 text-xl font-semibold text-gray-900 px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-400"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveTitle();
                    if (e.key === 'Escape') handleCancelEditTitle();
                  }}
                />
                <button
                  onClick={handleSaveTitle}
                  disabled={isSavingTitle}
                  className="p-2 bg-green-50 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                >
                  {isSavingTitle ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </button>
                <button
                  onClick={handleCancelEditTitle}
                  className="p-2 bg-gray-100 text-gray-500 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 group">
                <h1 className="text-xl font-semibold text-gray-900">{batch.title}</h1>
                <button
                  onClick={() => setIsEditingTitle(true)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                  title="Editar nome"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          <button
              onClick={handleDeleteBatch}
              className="p-2 text-gray-500 hover:text-red-600 bg-white border border-gray-200 hover:border-red-200 hover:bg-red-50 rounded-lg transition-colors"
              disabled={isDeleting}
              title="Excluir projeto"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Prompt (oculto por padrão) */}
          {batch.user_prompt && (
            <div>
              <button
                onClick={() => setShowPrompt(!showPrompt)}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showPrompt ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                <span>{showPrompt ? 'Ocultar prompt' : 'Ver prompt'}</span>
              </button>
              
              {showPrompt && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-sm text-gray-600 leading-relaxed">{batch.user_prompt}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Conteúdos */}
        {batch.contents && batch.contents.length > 0 ? (
          batch.batch_type === 'carousel' ? (
            /* Carousel View */
            <CarouselViewer
              batch={batch}
              contents={batch.contents}
              onPublish={(b) => {
                // Publicar carrossel completo - abre modal com primeiro content
                const firstCompleted = b.contents.find(c => c.status === 'completed');
                if (firstCompleted) {
                  setContentToPublish({ ...firstCompleted, _isCarousel: true, _batch: b });
                  setPublishModalOpen(true);
                }
              }}
            />
          ) : (
            /* Grid View (default) — layout adaptativo ao nº de artes pra não
               deixar 1 imagem "sozinha" num grid de 3 colunas. */
            (() => {
            const visibleCount = batch.contents.filter(c => c.status !== "failed").length;
            const gridClass =
              visibleCount <= 1
                ? "grid grid-cols-1 gap-4 auto-rows-[480px] max-w-sm mx-auto"
                : visibleCount === 2
                ? "grid grid-cols-1 sm:grid-cols-2 gap-4 auto-rows-[380px] max-w-2xl mx-auto"
                : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[280px]";
            return (
            <div className={gridClass}>
              {batch.contents.map((content, index) => {
                let gridSpan = getGridSpan(index, batch.contents.length);

                if (content.type === 'video') {
                  gridSpan = { col: 'col-span-1', row: 'row-span-2' }
                }

                return content.status !== "failed" && (
                  <ContentCard
                    key={content.id}
                    content={content}
                    gridSpan={gridSpan}
                    onPreview={handlePreview}
                    onDelete={null}
                    onPublish={content.status === 'completed' ? handleOpenPublishModal : null}
                    batchId={batchId}
                  />
                );
              })}
            </div>
            );
            })()
          )
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
            <p className="text-gray-500">Nenhum conteúdo neste projeto ainda.</p>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewContent && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closePreview}
        >
          <button
            onClick={closePreview}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* ✅ Fase 4: botão "Editar com IA" — só pra gpt_image (tela dedicada) */}
          {previewContent.model === 'gpt_image' && previewContent.status === 'completed' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/contents/batch/${batchId}/edit-gpt-image/${previewContent.id}`);
              }}
              className="absolute top-4 left-4 inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg text-white text-sm font-semibold shadow-lg transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Editar com IA
            </button>
          )}

          <div
            className="relative max-w-5xl w-full max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {previewContent.type === 'video' ? (
              <video
                src={previewContent.result_url}
                controls
                autoPlay
                className="max-w-full max-h-[90vh] rounded-xl"
              />
            ) : (
              <img
                src={previewContent.result_url}
                alt="Preview"
                className="max-w-full max-h-[90vh] rounded-xl"
              />
            )}
          </div>
        </div>
      )}

      {/* Modal de Publicação */}
      <PublishToSocialModal
        isOpen={publishModalOpen}
        onClose={handleClosePublishModal}
        content={contentToPublish}
        batchId={batchId}
        onPublishSuccess={handlePublishSuccess}
      />
    </div>
  );
};

export default ContentBatchViewer;